import type { KeyBinding } from '@/types/keyboard';

// Detect if user is on Mac (for event handling - excludes iOS)
const isMac = () => {
  if (typeof navigator === 'undefined') return false;
  // iOS devices can report as MacIntel, so check for touch points to exclude them
  const isIOSDevice = navigator.maxTouchPoints > 1 && 
                      (navigator.platform === 'MacIntel' || 
                       /iPad|iPhone|iPod/.test(navigator.userAgent));
  if (isIOSDevice) return false;
  return navigator.platform.toUpperCase().indexOf('MAC') >= 0;
};

// Detect if we should use Mac-style symbols for display (includes iOS/iPadOS)
const shouldUseMacSymbols = () => {
  if (typeof navigator === 'undefined') return false;
  // Use Mac symbols for actual Macs and iOS/iPadOS devices
  const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  return isMac || isIOSDevice;
};

// Convert KeyBinding to human-readable string
export function formatKeyBinding(binding: KeyBinding): string {
  const parts: string[] = [];
  const useSymbols = shouldUseMacSymbols();

  if (binding.ctrl) parts.push(useSymbols ? '⌘' : 'Ctrl');
  if (binding.alt) parts.push(useSymbols ? '⌥' : 'Alt');
  if (binding.shift) parts.push(useSymbols ? '⇧' : 'Shift');
  if (binding.meta) parts.push('Meta');

  // Filter out modifier keys from the main key (can happen on mobile)
  // Check case-insensitively for common modifier key names
  const modifierKeyNames = ['control', 'alt', 'shift', 'meta', 'command', 'ctrl'];
  const keyLower = binding.key.toLowerCase();
  const isModifierKey = modifierKeyNames.includes(keyLower);
  
  if (!isModifierKey && binding.key) {
    parts.push(formatKey(binding.key));
  }

  return parts.length > 0 ? parts.join(useSymbols ? '' : '+') : '';
}

function formatKey(key: string): string {
  const keyMap: Record<string, string> = {
    ArrowUp: '↑',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
    Enter: '↵',
    Escape: 'Esc',
    Delete: 'Del',
    Backspace: '⌫',
    ' ': 'Space',
  };

  return keyMap[key] || key.toUpperCase();
}

// Check if keyboard event matches a binding
export function matchesBinding(event: KeyboardEvent, binding: KeyBinding): boolean {
  const mac = isMac();

  // On Mac, ctrl maps to metaKey (Cmd), on others it's ctrlKey
  const ctrlPressed = mac ? event.metaKey : event.ctrlKey;

  // Single-character keys must compare case-insensitively: with Shift held,
  // event.key is uppercase ('R') while bindings store the lowercase letter ('r').
  // The shift modifier check below still disambiguates 'r' from Shift+R.
  const keyMatches =
    event.key.length === 1 && binding.key.length === 1
      ? event.key.toLowerCase() === binding.key.toLowerCase()
      : event.key === binding.key;

  // `meta` is the physical Meta/Win key, only meaningful on non-Mac. On Mac the
  // metaKey (Cmd) is already consumed by the `ctrl` abstraction above, so we must
  // NOT separately require binding.meta there — doing so would break every
  // Cmd-based shortcut on macOS. On other platforms, honor binding.meta so a
  // binding can target (or exclude) the Windows/Meta key (audit §2.17 #7).
  const metaMatches = mac ? true : !!binding.meta === event.metaKey;

  return (
    keyMatches &&
    !!binding.ctrl === ctrlPressed &&
    !!binding.alt === event.altKey &&
    !!binding.shift === event.shiftKey &&
    metaMatches
  );
}

// Parse keyboard event into KeyBinding
export function eventToBinding(event: KeyboardEvent): KeyBinding {
  const mac = isMac();

  return {
    key: event.key,
    ctrl: mac ? event.metaKey : event.ctrlKey,
    alt: event.altKey,
    shift: event.shiftKey,
  };
}

/**
 * Whether keyboard focus is currently inside a text-input context. For
 * commands that are whitelisted through the Escape input guard below but must
 * not fire while the user is typing (F8: Escape inside an inline reply —
 * which doesn't set composeOpen — tore down the sender group view).
 */
export function isTypingContext(): boolean {
  if (typeof document === 'undefined') return false;
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
}

// Check if an element should be excluded from keyboard shortcuts
export function shouldIgnoreEvent(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement;

  const isInInputContext =
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.isContentEditable;

  // If not in an input context, allow all shortcuts
  if (!isInInputContext) {
    return false;
  }

  // In input contexts, preserve native text editing shortcuts
  // These should NEVER be intercepted by custom handlers
  const mac = isMac();
  const hasCtrlOrCmd = mac ? event.metaKey : event.ctrlKey;
  const nativeEditingKeys = ['a', 'c', 'v', 'x', 'z', 'y']; // select all, copy, paste, cut, undo, redo

  if (hasCtrlOrCmd && nativeEditingKeys.includes(event.key.toLowerCase())) {
    return true; // Ignore this event, let browser handle it natively
  }

  // In input contexts, allow these keys to pass through to our handlers:
  // - Escape (to close/blur)
  // - Any key with Ctrl/Cmd modifier (for shortcuts like Ctrl+Enter to send)
  // - Any key with Alt modifier
  const hasModifier = mac ? event.metaKey || event.altKey : event.ctrlKey || event.altKey;

  const allowedKeys = ['Escape'];
  const isAllowedKey = allowedKeys.includes(event.key);

  // Allow: Escape or any shortcut with modifier keys
  if (isAllowedKey || hasModifier) {
    return false;
  }

  // Block: Single character keys (a-z, 0-9, etc.) when typing
  return true;
}
